import { useEffect, useState } from "react";

import {
  Avatar,
  Box,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";

import MemberCard from "./MemberCard";

import { Member, useAppStore } from "~/store";

type InitialMemberMode = "userMember" | "otherMembers";

export const InitialMemberDialogue: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [mode, setMode] = useState<InitialMemberMode>("userMember");
  const [addedMembersCount, setAddedMembersCount] = useState(0);
  const [createdMembers, setCreatedMembers] = useState<Member[]>([]);
  const { user } = useUser();
  const setCurrentMember = useAppStore((state) => state.setCurrentMember);
  const setEditMode = useAppStore((state) => state.setEditMode);
  const setMemberEntryMode = useAppStore((state) => state.setMemberEntryMode);
  const debugMode = useAppStore((state) => state.debugMode);
  const toast = useToast();

  // Set up keyboard listener for the 'i' key and listen for custom event
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if the key is pressed while any input element is focused
      if (
        e.key === "i" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes((document.activeElement?.tagName || "").toUpperCase())
      ) {
        if (isOpen) {
          handleClose();
        } else {
          setMode("userMember");
          onOpen();
        }
      }
    };

    // Add listener for custom event to open the dialog
    const handleOpenEvent = () => {
      setMode("userMember");
      onOpen();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("open-initial-member-dialogue", handleOpenEvent);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("open-initial-member-dialogue", handleOpenEvent);
    };
  }, [isOpen, onOpen]);

  // Initialize members based on the current mode
  const getUserMember = (): Member => ({
    id: 0,
    name: "",
    birthdate: "",
    gender: "F",
    email: user?.emailAddresses[0]?.emailAddress || "",
    profileImageUrl: ""
  });

  const getOtherMember = (): Member => ({
    id: 0,
    name: "",
    birthdate: "",
    gender: "F",
    email: "",
    profileImageUrl: ""
  });

  // Set up the initial member when the component mounts or mode changes
  useEffect(() => {
    if (isOpen) {
      // Set different initial data based on the mode
      const initialMember = mode === "userMember" ? getUserMember() : getOtherMember();
      setCurrentMember(initialMember);
      setEditMode(true);
      setMemberEntryMode(mode === "userMember" ? "addSelfInit" : "addMembersInit");
    }
  }, [isOpen, mode, setCurrentMember, setEditMode, setMemberEntryMode, user]);

  // Handle successful member creation
  const handleMemberCreated = (createdMember: Member) => {
    // Add the newly created member to our list
    setCreatedMembers((prev) => [...prev, createdMember]);

    // If we're in userMember mode, switch to otherMembers mode
    if (mode === "userMember") {
      setMode("otherMembers");
      setAddedMembersCount(1); // Count the user as the first member

      // Small delay to ensure UI updates correctly before resetting the form
      setTimeout(() => {
        // Set a new empty member for the "other members" mode
        setCurrentMember(getOtherMember());
        setEditMode(true);
        setMemberEntryMode("addMembersInit");
      }, 100);
    } else {
      // We're already in otherMembers mode, increment the counter
      setAddedMembersCount((count) => count + 1);

      // Reset the form to create another member
      setTimeout(() => {
        // Set a new empty member for creating another member
        setCurrentMember(getOtherMember());
        setEditMode(true);
        setMemberEntryMode("addMembersInit");
      }, 100);

      // Show success message (only show here, not in MemberCard component)
      toast({
        title: "Member added",
        description: "You can continue adding members or click Cancel when done.",
        status: "success",
        duration: 3000,
        isClosable: true,
        id: `member-added-${Date.now()}` // Add unique ID to prevent duplicate toasts
      });
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setCurrentMember(undefined);
    setEditMode(false);
    setMemberEntryMode("default");
    setAddedMembersCount(0); // Reset the counter
    setCreatedMembers([]); // Clear the members list
    onClose();
  };

  // Handle skipping user member creation
  const handleSkipUserMember = () => {
    if (mode === "userMember") {
      setMode("otherMembers");
      setAddedMembersCount(0); // No user added

      // Small delay to ensure UI updates correctly before setting new form
      setTimeout(() => {
        // Set a new empty member for the "other members" mode
        setCurrentMember(getOtherMember());
        setEditMode(true);
        setMemberEntryMode("addMembersInit");
      }, 100);
    }
  };

  // Handle cancel button in MemberCard
  const handleCancel = () => {
    handleClose();
  };

  const textColor = useColorModeValue("gray.700", "gray.200");
  const memberListBg = useColorModeValue("gray.50", "gray.700");
  const memberItemBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="2xl" fontWeight="bold" textAlign="center" color={textColor}>
            {mode === "userMember"
              ? "Tell us about yourself"
              : `Add another member ${addedMembersCount > 0 ? `(${addedMembersCount} added)` : ""}`}
          </Text>
          <Text fontSize="sm" textAlign="center" color="gray.500" mt={2}>
            {mode === "userMember"
              ? "Please enter your information to get started with Henophess."
              : "Let's add the members whose health metrics you will be tracking and storing with Henophess. This could be your family, a clinic, or any group of people."}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex direction={{ base: "column", md: "row" }} gap={6}>
            {/* Left side - Member form */}
            <Box flex="1" maxW={{ base: "100%", md: "450px" }} mx="auto">
              <MemberCard
                onMemberCreated={handleMemberCreated}
                suppressSuccessToast={true}
                mode={mode}
                isUserMember={mode === "userMember"}
                onSkip={mode === "userMember" ? handleSkipUserMember : undefined}
                onCancel={handleCancel}
              />
              <Text fontSize="sm" textAlign="center" color="gray.500" mt={4}>
                We need this information to accurately track health metrics such as pH and RBTI analysis, and provide
                personalized recommendations.
              </Text>
            </Box>

            {/* Right side - Members list (always visible, empty when no members) */}
            <Box
              flex="0.33"
              bg={memberListBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              maxH="400px"
              height="400px"
              overflowY="auto"
              css={{
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  width: "10px",
                  background: "transparent"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: useColorModeValue("rgba(0,0,0,0.2)", "rgba(255,255,255,0.2)"),
                  borderRadius: "24px"
                }
              }}
            >
              <Text fontWeight="bold" mb={2} fontSize="sm">
                Added Members ({createdMembers.length})
              </Text>
              <Divider mb={2} />
              {createdMembers.length > 0 ? (
                <VStack spacing={2} align="stretch">
                  {createdMembers.map((member, index) => (
                    <Box
                      key={index}
                      p={2}
                      borderRadius="md"
                      bg={memberItemBg}
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack spacing={2}>
                        <Avatar size="xs" name={member.name} src={member.profileImageUrl} />
                        <Box>
                          <Text fontWeight="medium" fontSize="sm">
                            {member.name}
                          </Text>
                          {member.email && (
                            <Text fontSize="xs" color="gray.500">
                              {member.email}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Box textAlign="center" py={10}>
                  <Text color="gray.500" fontSize="sm">
                    No members added yet.
                  </Text>
                  <Text color="gray.500" fontSize="sm" mt={2}>
                    Complete the form on the left to add your first member.
                  </Text>
                </Box>
              )}
            </Box>
          </Flex>
        </ModalBody>
        {/* Remove ModalFooter entirely since we don't need any buttons there */}
      </ModalContent>
    </Modal>
  );
};

export default InitialMemberDialogue;
